import { EApiLanguageCode, EApiTest } from '@datatype/iyagigo/api-type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { useEffect, useRef } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as studentActions } from '@store/slice/student-slice';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';

// ###################################
// stable sub menu
// - bottom tab 이동 직전 한 프레임 동안 다른 메뉴의 subMenu 값이 들어와
//   현재 페이지 탭이 unselected(흰색)로 깜빡이는 것 방지
// - 현재 페이지의 subMenu ids에 속하는 값일 때만 ref 갱신, 아니면 마지막 값 유지
// ###################################
export const useCurrentSubMenu = <T extends string>(validIds: readonly T[], defaultId: T): T => {
  const currentSubMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu);
  const currentSubMenuRef = useRef<T>(defaultId);
  if (validIds.includes(currentSubMenu as T)) {
    currentSubMenuRef.current = currentSubMenu as T;
  }
  return currentSubMenuRef.current;
};

// ###################################
// test menu sync
// - /ig/test/home path <-> navigator sub menu 동기화
// - /ig/test/problem?language=en&subject=toeic850 path <-> navigator sub menu 동기화
// ###################################
export const useTestPageSync = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const match = useMatch(IYAGIGO_PATH.TEST_PATH);
  const path = match?.params.test;
  const language = searchParams.get('language');
  const subject = searchParams.get('subject')?.toUpperCase() as EApiTest | undefined;

  useEffect(() => {
    if (!match) return;
    if (path === 'home') {
      dispatch(navigationActions.storeIyagigoMainMenu('test'));
      return;
    }
    if (language === EApiLanguageCode.ENGLISH.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.ENGLISH));
      if (subject?.startsWith('TOEIC')) dispatch(studentActions.storeCurrentToeicTest(subject));
    }
    if (language === EApiLanguageCode.JAPANESE.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.JAPANESE));
      if (subject?.startsWith('JLPT')) dispatch(studentActions.storeCurrentJlptTest(subject));
    }
  }, [language, path]);
};

/**
 * ig/hub/:hub path,page <-> navigator sub menu 동기화
 */
export const useHubPageSync = () => {
  const dispatch = useTypedDispatch();
  const match = useMatch(IYAGIGO_PATH.HUB_PATH);
  const hub = match?.params.hub as TIyagigoSubMenu['hub'];
  const subMenu = ['contact', 'account', 'subscription', 'help', 'application-metadata'].includes(hub ?? '')
    ? hub
    : null;
  useEffect(() => {
    if (!match) return;
    if (!hub) return;
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'hub', subMenu: subMenu }));
  }, [hub]);
};

// ###################################
// home (Hub) menu sync
// - ig/home/:home path <-> navigator main/sub menu 동기화
// ###################################
export const useHomePageSync = () => {
  const dispatch = useTypedDispatch();
  const match = useMatch(IYAGIGO_PATH.HOME_PATH);
  const home = match?.params.home as TIyagigoSubMenu['home'];

  useEffect(() => {
    if (!match) return;
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'home', subMenu: home }));
  }, [home]);
};

// ###################################
// cloze menu sync
// - ig/cloze/home path <-> navigator main menu 동기화
// - ig/cloze/study?language=ja&subject=vocabulary path <-> navigator sub menu 동기화
// - ig/cloze/note path <-> navigator sub menu 동기화
// ###################################
export const useClozePageSync = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const match = useMatch(IYAGIGO_PATH.CLOZE_PATH);
  const path = match?.params.cloze;
  const searchParams = new URLSearchParams(location.search);
  const language = searchParams.get('language');
  const subject = searchParams.get('subject') as TIyagigoSubMenu['cloze'];

  useEffect(() => {
    if (!match) return;
    if (path === 'home') {
      dispatch(navigationActions.storeIyagigoMainMenu('cloze'));
      return;
    }
    if (language === EApiLanguageCode.ENGLISH.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.ENGLISH));
    }
    if (language === EApiLanguageCode.JAPANESE.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.JAPANESE));
    }
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'cloze', subMenu: subject }));
  }, [language, path]);
};

// ###################################
// vocabulary-book menu sync
// - ig/vocabulary-book/home path <-> navigator main menu 동기화
// - ig/vocabulary-book/study?language=ja path <-> navigator sub menu 동기화
// ###################################
export const useVocabularyBookPageSync = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const match = useMatch(IYAGIGO_PATH.VOCABULARY_BOOK_PATH);
  const path = match?.params.vocabularyBook;
  const searchParams = new URLSearchParams(location.search);
  const language = searchParams.get('language');
  const subject = searchParams.get('subject') as TIyagigoSubMenu['vocabulary-book'];

  useEffect(() => {
    if (!match) return;
    if (path === 'home') {
      dispatch(navigationActions.storeIyagigoMainMenu('vocabulary-book'));
      return;
    }
    if (language === EApiLanguageCode.ENGLISH.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.ENGLISH));
    }
    if (language === EApiLanguageCode.JAPANESE.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.JAPANESE));
    }
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'vocabulary-book', subMenu: subject }));
  }, [language, path]);
};

// ###################################
// classroom menu sync (일본어교실 — 히라가나/기초문법 입문)
// - ig/classroom/home path <-> navigator main menu 동기화
// - ig/classroom/character?language=ja path <-> navigator sub menu 동기화 (글자: 히라가나·가타카나)
// - ig/classroom/grammar?language=ja path <-> navigator sub menu 동기화 (예정)
// ###################################
export const usClassroomPageSync = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const match = useMatch(IYAGIGO_PATH.CLASSROOM_PATH);
  const path = match?.params.classroom;
  const searchParams = new URLSearchParams(location.search);
  const language = searchParams.get('language');
  const subject = path as TIyagigoSubMenu['classroom'];

  useEffect(() => {
    if (!match) return;
    if (path === 'home') {
      dispatch(navigationActions.storeIyagigoMainMenu('classroom'));
      return;
    }
    if (language === EApiLanguageCode.ENGLISH.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.ENGLISH));
    }
    if (language === EApiLanguageCode.JAPANESE.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.JAPANESE));
    }
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'classroom', subMenu: subject }));
  }, [language, path]);
};

// ###################################
// clip menu sync (일상표현 — 생생표현/간편이야기)
// - ig/clip/home path <-> navigator main menu 동기화
// - ig/clip/authentic-expression?language=ja path <-> navigator sub menu 동기화
// - ig/clip/mini-story?language=ja path <-> navigator sub menu 동기화
// ###################################
export const useClipPageSync = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const match = useMatch(IYAGIGO_PATH.CLIP_PATH);
  const path = match?.params.clip;
  const searchParams = new URLSearchParams(location.search);
  const language = searchParams.get('language');
  const subject = path as TIyagigoSubMenu['clip'];

  useEffect(() => {
    if (!match) return;
    if (path === 'home') {
      dispatch(navigationActions.storeIyagigoMainMenu('clip'));
      return;
    }
    if (language === EApiLanguageCode.ENGLISH.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.ENGLISH));
    }
    if (language === EApiLanguageCode.JAPANESE.toLowerCase()) {
      dispatch(studentActions.storeCurrentStudyLanguage(EApiLanguageCode.JAPANESE));
    }
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'clip', subMenu: subject }));
  }, [language, path]);
};

// ###################################
// speaking menu sync
// - /ig/speaking/home path <-> navigator main/sub menu 동기화
// ###################################
export const useSpeakingPageSync = () => {
  const dispatch = useTypedDispatch();
  const match = useMatch(IYAGIGO_PATH.SPEAKING_PATH);
  const speaking = match?.params.speaking as TIyagigoSubMenu['speaking'];

  useEffect(() => {
    if (!match) return;
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'speaking', subMenu: speaking }));
  }, [speaking]);
};
